import React, { useEffect, useRef, useState } from 'react';
import './CampaignLanding.css';
import { sendCampaignLead } from '../services/api';
import { getTrackingIdentity, trackTelemetryEvent } from '../utils/anonymousTelemetry';

const PHONE_NUMBER_DISPLAY = '(984) 234-7030';
const PHONE_NUMBER_LINK = 'tel:+19842347030';
const GOOGLE_REVIEWS_URL = 'https://maps.app.goo.gl/6GHASTC4zo8rH52y7';
const CALL_START_KEY = 'rkube_campaign_call_started_at';
const ATTRIBUTION_STORAGE_KEY = 'rkube_campaign_attribution';
const SCROLL_MILESTONES = [25, 50, 75, 90];
const ATTRIBUTION_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'gclid',
  'hsa_acc',
  'hsa_cam',
  'hsa_grp',
  'hsa_ad',
  'hsa_src',
  'hsa_tgt',
  'hsa_kw',
  'hsa_mt',
  'hsa_net',
  'hsa_ver'
];
const FORM_REQUIRED_FIELDS = ['name', 'business', 'email', 'phone', 'serviceFocus', 'monthlyVolume', 'timeline'];
// Only real Google Ads actions in account AW-17465397731. Page views and clicks stay GA4 events
// (phone clicks are counted by the GTM phone_click tag), so bidding sees one lead per form.
const ADS_CONVERSION_IDS = {
  landingView: null,
  callClick: null,
  formSubmit: null,
  leadSuccess: 'AW-17465397731/POpFCI6RsYUbEOOjk4hB'
};

function getDeviceType() {
  const width = typeof window !== 'undefined' ? window.innerWidth : 0;
  if (width <= 767) return 'mobile';
  if (width <= 1024) return 'tablet';
  return 'desktop';
}

function getDeviceContext() {
  if (typeof window === 'undefined') {
    return {};
  }
  return {
    device_type: getDeviceType(),
    user_agent: navigator.userAgent || 'unknown',
    language: navigator.language || 'unknown',
    platform: navigator.platform || 'unknown',
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    screen_resolution: `${window.screen?.width || 0}x${window.screen?.height || 0}`,
    pixel_ratio: window.devicePixelRatio || 1
  };
}

function getScrollPercent() {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return 0;
  }
  const scrollTop = window.scrollY || document.documentElement.scrollTop || 0;
  const docHeight = Math.max(document.documentElement.scrollHeight - window.innerHeight, 1);
  return Math.max(0, Math.min(100, Math.round((scrollTop / docHeight) * 100)));
}

function readSavedAttribution() {
  try {
    const saved = sessionStorage.getItem(ATTRIBUTION_STORAGE_KEY);
    return saved ? JSON.parse(saved) : {};
  } catch (error) {
    return {};
  }
}

function readQueryAttribution() {
  const urlParams = new URLSearchParams(window.location.search);
  return ATTRIBUTION_KEYS.reduce((acc, key) => {
    const value = urlParams.get(key);
    if (value) {
      acc[key] = value;
    }
    return acc;
  }, {});
}

function readAttribution() {
  const savedAttribution = readSavedAttribution();
  const queryAttribution = readQueryAttribution();
  const merged = ATTRIBUTION_KEYS.reduce((acc, key) => {
    acc[key] = queryAttribution[key] || savedAttribution[key] || undefined;
    return acc;
  }, {});
  const hasQueryAttribution = Object.keys(queryAttribution).length > 0;

  if (hasQueryAttribution) {
    sessionStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(merged));
  }
  return merged;
}

function pushTrackingEvent(eventName, payload, options = {}) {
  void trackTelemetryEvent(eventName, payload, {
    emitGtag: false,
    ...options
  });
}

function trackGtagEvent(eventName, payload = {}) {
  if (typeof window !== 'undefined' && typeof window.gtag !== 'undefined') {
    window.gtag('event', eventName, payload);
  }
}

function trackAdsConversion(sendTo, payload = {}) {
  if (!sendTo) return;
  trackGtagEvent('conversion', {
    send_to: sendTo,
    ...payload
  });
}

function CampaignLanding() {
  const [formData, setFormData] = useState({
    name: '',
    business: '',
    email: '',
    phone: '',
    serviceFocus: '',
    monthlyVolume: '',
    timeline: '',
    notes: ''
  });
  const [formState, setFormState] = useState({
    loading: false,
    success: false,
    error: ''
  });
  const hasTrackedFormStartRef = useRef(false);
  const sessionIdRef = useRef(`ccta_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`);
  const anonIdRef = useRef('');
  const anonFpIdRef = useRef('');
  const sessionStartedAtRef = useRef(Date.now());
  const firstInteractionAtRef = useRef(null);
  const interactionCountRef = useRef(0);
  const maxScrollPercentRef = useRef(0);
  const seenScrollMilestonesRef = useRef(new Set());
  const reachedFormSectionRef = useRef(false);
  const formTouchedFieldsRef = useRef(new Set());
  const formLastProgressSignatureRef = useRef('');
  const lastCallClickAtRef = useRef(0);
  const hasTrackedPointerRef = useRef(false);
  const hasTrackedTouchRef = useRef(false);
  const deviceContextRef = useRef(getDeviceContext());

  const markInteraction = () => {
    interactionCountRef.current += 1;
    if (!firstInteractionAtRef.current) {
      firstInteractionAtRef.current = Date.now();
    }
  };

  const buildSessionTelemetry = () => {
    const elapsedSeconds = Math.max(0, Math.round((Date.now() - sessionStartedAtRef.current) / 1000));
    const firstInteractionSeconds = firstInteractionAtRef.current
      ? Math.max(0, Math.round((firstInteractionAtRef.current - sessionStartedAtRef.current) / 1000))
      : null;
    return {
      session_id: sessionIdRef.current,
      anon_id: anonIdRef.current || undefined,
      anon_fp_id: anonFpIdRef.current || undefined,
      elapsed_seconds: elapsedSeconds,
      first_interaction_seconds: firstInteractionSeconds,
      interaction_count: interactionCountRef.current,
      max_scroll_percent: maxScrollPercentRef.current,
      session_end_ts: new Date().toISOString(),
      ...deviceContextRef.current
    };
  };

  const emitFormProgress = (attribution, formStateData = formData) => {
    const requiredDone = FORM_REQUIRED_FIELDS.reduce((count, field) => {
      return formStateData[field] && String(formStateData[field]).trim() ? count + 1 : count;
    }, 0);
    const touched = formTouchedFieldsRef.current.size;
    const signature = `${touched}:${requiredDone}:${formStateData.notes ? 1 : 0}`;
    if (signature === formLastProgressSignatureRef.current) {
      return;
    }
    formLastProgressSignatureRef.current = signature;

    const telemetry = buildSessionTelemetry();
    const payload = {
      page_type: 'bookkeeping_call_campaign',
      fields_touched_count: touched,
      required_fields_completed_count: requiredDone,
      full_form_no_submit: requiredDone === FORM_REQUIRED_FIELDS.length,
      ...telemetry,
      ...attribution
    };

    pushTrackingEvent('campaign_form_progress', payload);
    trackGtagEvent('campaign_form_progress', payload);
  };

  useEffect(() => {
    const attribution = readAttribution();
    const sessionTelemetry = buildSessionTelemetry();
    window.utmData = attribution;
    void getTrackingIdentity().then((identity) => {
      anonIdRef.current = identity.anon_id;
      anonFpIdRef.current = identity.anon_fp_id;
      sessionIdRef.current = identity.session_id;
    });

    pushTrackingEvent('campaign_page_view', {
      page_type: 'bookkeeping_call_campaign',
      page_path: '/campaign',
      phone_number: PHONE_NUMBER_DISPLAY,
      ...sessionTelemetry,
      ...attribution
    });

    trackGtagEvent('campaign_page_view', {
      page_type: 'bookkeeping_call_campaign',
      page_path: '/campaign',
      session_id: sessionTelemetry.session_id,
      device_type: sessionTelemetry.device_type,
      viewport: sessionTelemetry.viewport,
      ...attribution
    });

    trackAdsConversion(ADS_CONVERSION_IDS.landingView, {
      custom_parameters: {
        source: attribution.utm_source || 'direct',
        medium: attribution.utm_medium || 'none',
        campaign: attribution.utm_campaign || 'none',
        hsa_cam: attribution.hsa_cam || 'none',
        hsa_ad: attribution.hsa_ad || 'none',
        hsa_tgt: attribution.hsa_tgt || 'none'
      }
    });

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        const clickAt = lastCallClickAtRef.current;
        if (clickAt > 0 && Date.now() - clickAt < 8000) {
          pushTrackingEvent('phone_call_prompt_dismissed', {
            page_type: 'bookkeeping_call_campaign',
            prompt_elapsed_seconds: Math.max(0, Math.round((Date.now() - clickAt) / 1000)),
            ...buildSessionTelemetry(),
            ...attribution
          });
          trackGtagEvent('phone_call_prompt_dismissed', {
            page_type: 'bookkeeping_call_campaign',
            ...buildSessionTelemetry(),
            ...attribution
          });
          lastCallClickAtRef.current = 0;
        }

        const startedAt = Number(sessionStorage.getItem(CALL_START_KEY) || '0');
        if (startedAt > 0) {
          const durationSeconds = Math.max(0, Math.round((Date.now() - startedAt) / 1000));
          sessionStorage.removeItem(CALL_START_KEY);

          pushTrackingEvent('phone_call_return', {
            page_type: 'bookkeeping_call_campaign',
            phone_number: PHONE_NUMBER_DISPLAY,
            estimated_call_duration_seconds: durationSeconds
          });

          trackGtagEvent('phone_call_return', {
            event_category: 'engagement',
            event_label: 'Campaign Phone Return',
            value: durationSeconds,
            ...attribution
          });
        }
      } else {
        const telemetry = buildSessionTelemetry();
        pushTrackingEvent('campaign_session_hidden', {
          page_type: 'bookkeeping_call_campaign',
          ...telemetry,
          ...attribution
        });
      }
    };

    let scrollTicking = false;
    const handleScroll = () => {
      markInteraction();
      if (scrollTicking) return;
      scrollTicking = true;
      window.requestAnimationFrame(() => {
        const percent = getScrollPercent();
        maxScrollPercentRef.current = Math.max(maxScrollPercentRef.current, percent);

        SCROLL_MILESTONES.forEach((milestone) => {
          if (percent >= milestone && !seenScrollMilestonesRef.current.has(milestone)) {
            seenScrollMilestonesRef.current.add(milestone);
            const telemetry = buildSessionTelemetry();
            pushTrackingEvent('campaign_scroll_depth', {
              page_type: 'bookkeeping_call_campaign',
              scroll_depth_percent: milestone,
              ...telemetry,
              ...attribution
            });
            trackGtagEvent('scroll', {
              percent_scrolled: milestone,
              session_id: telemetry.session_id,
              ...attribution
            });
          }
        });

        const formSection = document.getElementById('callback-form');
        if (formSection && !reachedFormSectionRef.current) {
          const rect = formSection.getBoundingClientRect();
          if (rect.top <= window.innerHeight * 0.8) {
            reachedFormSectionRef.current = true;
            const telemetry = buildSessionTelemetry();
            pushTrackingEvent('campaign_scroll_to_form', {
              page_type: 'bookkeeping_call_campaign',
              reached_form_section: true,
              ...telemetry,
              ...attribution
            });
            trackGtagEvent('campaign_scroll_to_form', {
              page_type: 'bookkeeping_call_campaign',
              ...telemetry,
              ...attribution
            });
          }
        }
        scrollTicking = false;
      });
    };

    const handlePointerDown = (event) => {
      markInteraction();
      if (!hasTrackedPointerRef.current) {
        hasTrackedPointerRef.current = true;
        const telemetry = buildSessionTelemetry();
        pushTrackingEvent('campaign_first_pointer', {
          page_type: 'bookkeeping_call_campaign',
          pointer_type: event.pointerType || 'mouse',
          x_bucket: Math.round((event.clientX || 0) / 50) * 50,
          y_bucket: Math.round((event.clientY || 0) / 50) * 50,
          ...telemetry,
          ...attribution
        });
        trackGtagEvent('campaign_first_pointer', {
          pointer_type: event.pointerType || 'mouse',
          session_id: telemetry.session_id,
          ...attribution
        });
      }
    };

    const handleTouchStart = () => {
      markInteraction();
      if (!hasTrackedTouchRef.current) {
        hasTrackedTouchRef.current = true;
        const telemetry = buildSessionTelemetry();
        pushTrackingEvent('campaign_first_touch', {
          page_type: 'bookkeeping_call_campaign',
          ...telemetry,
          ...attribution
        });
        trackGtagEvent('campaign_first_touch', {
          session_id: telemetry.session_id,
          ...attribution
        });
      }
    };

    const heartbeatInterval = window.setInterval(() => {
      const telemetry = buildSessionTelemetry();
      pushTrackingEvent('campaign_engagement_heartbeat', {
        page_type: 'bookkeeping_call_campaign',
        ...telemetry,
        ...attribution
      });
      trackGtagEvent('campaign_engagement_heartbeat', {
        session_id: telemetry.session_id,
        elapsed_seconds: telemetry.elapsed_seconds,
        max_scroll_percent: telemetry.max_scroll_percent,
        interaction_count: telemetry.interaction_count,
        ...attribution
      });
    }, 30000);

    const handlePageHide = () => {
      const telemetry = buildSessionTelemetry();
      pushTrackingEvent('campaign_session_end', {
        page_type: 'bookkeeping_call_campaign',
        ...telemetry,
        ...attribution
      });
      trackGtagEvent('campaign_session_end', {
        session_id: telemetry.session_id,
        elapsed_seconds: telemetry.elapsed_seconds,
        max_scroll_percent: telemetry.max_scroll_percent,
        interaction_count: telemetry.interaction_count,
        ...attribution
      });
    };

    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('pointerdown', handlePointerDown, { passive: true });
    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('pagehide', handlePageHide);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('pointerdown', handlePointerDown);
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('pagehide', handlePageHide);
      window.clearInterval(heartbeatInterval);
    };
  }, []);

  const handleCallClick = (placement) => {
    markInteraction();
    const attribution = readAttribution();
    const telemetry = buildSessionTelemetry();
    const clickedAtIso = new Date().toISOString();
    lastCallClickAtRef.current = Date.now();
    sessionStorage.setItem(CALL_START_KEY, String(Date.now()));

    pushTrackingEvent('phone_call_click', {
      page_type: 'bookkeeping_call_campaign',
      placement,
      phone_number: PHONE_NUMBER_DISPLAY,
      call_click_time_iso: clickedAtIso,
      user_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      ...telemetry,
      ...attribution
    });

    trackGtagEvent('click_to_call', {
      event_category: 'engagement',
      event_label: `Campaign Call Click - ${placement}`,
      placement,
      phone_number: PHONE_NUMBER_DISPLAY,
      click_time_iso: clickedAtIso,
      session_id: telemetry.session_id,
      ...attribution
    });

    trackAdsConversion(ADS_CONVERSION_IDS.callClick, {
      value: 1.0,
      currency: 'USD',
      custom_parameters: {
        click_location: placement,
        source: attribution.utm_source || 'direct',
        campaign: attribution.utm_campaign || 'none',
        hsa_cam: attribution.hsa_cam || 'none',
        hsa_ad: attribution.hsa_ad || 'none',
        hsa_tgt: attribution.hsa_tgt || 'none'
      }
    });
  };

  const handleCallbackJump = () => {
    markInteraction();
    const attribution = readAttribution();
    const telemetry = buildSessionTelemetry();
    pushTrackingEvent('callback_jump_click', {
      page_type: 'bookkeeping_call_campaign',
      placement: 'hero_secondary',
      ...telemetry,
      ...attribution
    });

    trackGtagEvent('callback_jump_click', {
      event_category: 'engagement',
      event_label: 'Campaign Callback Jump',
      session_id: telemetry.session_id,
      ...attribution
    });
  };

  const handleReviewsClick = () => {
    markInteraction();
    const attribution = readAttribution();
    const telemetry = buildSessionTelemetry();
    pushTrackingEvent('google_reviews_click', {
      page_type: 'bookkeeping_call_campaign',
      placement: 'reviews_section',
      ...telemetry,
      ...attribution
    });
    trackGtagEvent('google_reviews_click', {
      placement: 'reviews_section',
      session_id: telemetry.session_id,
      ...attribution
    });
  };

  const handleInputChange = (event) => {
    markInteraction();
    const { name, value } = event.target;
    formTouchedFieldsRef.current.add(name);
    const nextFormData = { ...formData, [name]: value };
    setFormData(nextFormData);
    const attribution = readAttribution();
    window.setTimeout(() => emitFormProgress(attribution, nextFormData), 0);
  };

  const handleFormStart = (fieldName) => {
    formTouchedFieldsRef.current.add(fieldName);
    if (hasTrackedFormStartRef.current) {
      const attribution = readAttribution();
      window.setTimeout(() => emitFormProgress(attribution), 0);
      return;
    }
    hasTrackedFormStartRef.current = true;
    const attribution = readAttribution();
    const telemetry = buildSessionTelemetry();
    pushTrackingEvent('campaign_form_start', {
      page_type: 'bookkeeping_call_campaign',
      field_name: fieldName,
      ...telemetry,
      ...attribution
    });
    trackGtagEvent('form_start', {
      form_name: 'campaign_callback_form',
      field_name: fieldName,
      session_id: telemetry.session_id,
      ...attribution
    });
  };

  const handleFormSubmit = async (event) => {
    event.preventDefault();
    setFormState({ loading: true, success: false, error: '' });

    const attribution = readAttribution();
    const telemetry = buildSessionTelemetry();
    const payload = {
      name: formData.name,
      email: formData.email,
      phone: formData.phone || undefined,
      message: [
        'Campaign lead: Bookkeeping callback request',
        `Business: ${formData.business || 'Not provided'}`,
        `Looking for: ${formData.serviceFocus || 'Not provided'}`,
        `Monthly volume: ${formData.monthlyVolume || 'Not provided'}`,
        `Start timeline: ${formData.timeline || 'Not provided'}`,
        `Notes: ${formData.notes || 'Not provided'}`,
        `Source: ${attribution.utm_source || 'direct'}`,
        `Medium: ${attribution.utm_medium || 'none'}`,
        `Campaign: ${attribution.utm_campaign || 'none'}`,
        `Term: ${attribution.utm_term || 'none'}`,
        `GCLID: ${attribution.gclid || 'none'}`,
        '',
        'Client telemetry',
        `Session ID: ${telemetry.session_id}`,
        `Elapsed Seconds: ${telemetry.elapsed_seconds}`,
        `First Interaction Seconds: ${telemetry.first_interaction_seconds ?? 'none'}`,
        `Interaction Count: ${telemetry.interaction_count}`,
        `Max Scroll Percent: ${telemetry.max_scroll_percent}`,
        `Device Type: ${telemetry.device_type || 'unknown'}`,
        `Viewport: ${telemetry.viewport || 'unknown'}`,
        `Screen: ${telemetry.screen_resolution || 'unknown'}`,
        `Language: ${telemetry.language || 'unknown'}`,
        `Platform: ${telemetry.platform || 'unknown'}`
      ].join('\n')
    };

    try {
      await sendCampaignLead(payload);

      pushTrackingEvent('campaign_form_submit', {
        page_type: 'bookkeeping_call_campaign',
        lead_type: 'callback_request',
        ...telemetry,
        ...attribution
      });

      if (typeof window !== 'undefined' && typeof window.gtag !== 'undefined') {
        trackGtagEvent('form_submit', {
          form_name: 'campaign_callback_form',
          lead_type: 'callback_request',
          session_id: telemetry.session_id,
          ...attribution
        });
        trackGtagEvent('generate_lead', {
          event_category: 'engagement',
          event_label: 'Campaign Callback Form',
          value: 1,
          currency: 'USD',
          session_id: telemetry.session_id,
          ...attribution
        });
      }

      trackAdsConversion(ADS_CONVERSION_IDS.formSubmit, {
        value: 1.0,
        currency: 'USD',
        custom_parameters: {
          source: attribution.utm_source || 'direct',
          campaign: attribution.utm_campaign || 'none',
          hsa_cam: attribution.hsa_cam || 'none',
          hsa_ad: attribution.hsa_ad || 'none',
          hsa_tgt: attribution.hsa_tgt || 'none'
        }
      });

      trackAdsConversion(ADS_CONVERSION_IDS.leadSuccess, {
        value: 1.0,
        currency: 'USD',
        custom_parameters: {
          source: attribution.utm_source || 'direct',
          campaign: attribution.utm_campaign || 'none'
        }
      });

      setFormState({ loading: false, success: true, error: '' });
      setFormData({
        name: '',
        business: '',
        email: '',
        phone: '',
        serviceFocus: '',
        monthlyVolume: '',
        timeline: '',
        notes: ''
      });
    } catch (error) {
      pushTrackingEvent('campaign_form_submit_error', {
        page_type: 'bookkeeping_call_campaign',
        error_type: 'api_error'
      });
      trackGtagEvent('campaign_form_submit_error', {
        page_type: 'bookkeeping_call_campaign',
        error_type: 'api_error'
      });
      setFormState({
        loading: false,
        success: false,
        error: 'We could not submit your request. Please call us directly.'
      });
    }
  };

  return (
    <div className="campaign-landing">
      <section className="campaign-brand">
        <div className="campaign-wrap">
          <p>
            <strong>RKube Services</strong> | Bookkeeping by the <strong>RKube Tax & Accounting</strong> team
          </p>
        </div>
      </section>

      <section className="campaign-hero">
        <div className="campaign-wrap">
          <p className="campaign-kicker">RKube Services Bookkeeping For Small Businesses</p>
          <p className="campaign-trial-badge">30-Day Bookkeeping Trial | No Credit Card | No Obligation</p>
          <h1>Get Your First 30 Days Of Bookkeeping Free - Then Decide</h1>
          <p className="campaign-subtitle">
            If your books are messy or behind, we will clean and run your books for 30 days so you can see our quality before you pay.
          </p>
          <p className="campaign-price-callout">Plans start at $99/month*</p>
          <a
            className="campaign-call-btn"
            href={PHONE_NUMBER_LINK}
            onClick={() => handleCallClick('hero_primary')}
          >
            Claim My Free 30-Day Trial: {PHONE_NUMBER_DISPLAY}
          </a>
          <ul className="campaign-hero-proof">
            <li>Done-for-you monthly bookkeeping by a dedicated RKube team</li>
            <li>No credit card required to start your trial</li>
            <li>Clear handoff to ongoing monthly support only if you like the result</li>
          </ul>
          <p className="campaign-availability">Mon-Fri 8:00 AM - 8:00 PM, Sat 9:00 AM - 2:00 PM (local time)</p>
          <p className="campaign-callback-hint">
            Can't call now?{' '}
            <a href="#callback-form" onClick={handleCallbackJump}>
              Request a callback
            </a>
          </p>
        </div>
      </section>

      <section className="campaign-section">
        <div className="campaign-wrap campaign-grid">
          <article className="campaign-card">
            <h2>What You Get</h2>
            <ul>
              <li>30-day trial bookkeeping setup and cleanup</li>
              <li>Transaction categorization and reconciliation</li>
              <li>Owner-ready reports you can actually use</li>
              <li>A simple monthly plan recommendation after trial</li>
            </ul>
          </article>

          <article className="campaign-card">
            <h2>Who This Is For</h2>
            <ul>
              <li>Small business owners who are behind on books</li>
              <li>Teams tired of DIY bookkeeping and rework</li>
              <li>Founders who need reliable monthly numbers fast</li>
              <li>Businesses ready to outsource bookkeeping end-to-end</li>
            </ul>
          </article>
        </div>
      </section>

      <section className="campaign-section campaign-reviews">
        <div className="campaign-wrap">
          <div className="campaign-reviews-header">
            <p className="campaign-reviews-kicker">Google Reviews</p>
            <h2>Real Feedback From Business Owners</h2>
            <p className="campaign-reviews-subtitle">
              Clients consistently highlight fast communication, bookkeeping clarity, and practical tax support.
            </p>
            <a
              className="campaign-reviews-link"
              href={GOOGLE_REVIEWS_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleReviewsClick}
            >
              Read all reviews on Google
            </a>
          </div>

          <div className="campaign-reviews-grid">
            <article className="campaign-review-card">
              <h3>Anandamurugan Nagarajan</h3>
              <p>
                "I had an excellent experience working with the RKube team for bookkeeping and tax filing support.
                Their approach is highly organized, transparent, and tailored to the needs of growing businesses."
              </p>
            </article>

            <article className="campaign-review-card">
              <h3>Emily Zhu</h3>
              <p>
                "RKube is a breeze to work with. They gave me a free consultation to help me navigate small business
                formation, and I will be back for bookkeeping soon."
              </p>
            </article>

            <article className="campaign-review-card">
              <h3>Carldine Jean-Francois</h3>
              <p>"As a small business owner, I highly recommend!"</p>
            </article>
          </div>
        </div>
      </section>

      <section className="campaign-section campaign-note">
        <div className="campaign-wrap">
          <h2>Important</h2>
          <p>
            This line is for bookkeeping service inquiries. We do not provide QuickBooks software support,
            QuickBooks troubleshooting, or QuickBooks login assistance.
          </p>
        </div>
      </section>

      <section className="campaign-section campaign-final-cta">
        <div className="campaign-wrap">
          <h2>Want To See If We Are The Right Fit?</h2>
          <p>Start with a free 30-day trial and keep it only if it works for your business.</p>
          <a
            className="campaign-call-btn campaign-call-btn-large"
            href={PHONE_NUMBER_LINK}
            onClick={() => handleCallClick('footer_primary')}
          >
            Start Trial By Phone: {PHONE_NUMBER_DISPLAY}
          </a>
        </div>
      </section>

      <section id="callback-form" className="campaign-section campaign-form-section">
        <div className="campaign-wrap campaign-form-wrap">
          <div className="campaign-form-copy">
            <h2>Prefer A Callback Instead?</h2>
            <p>
              Answer 3 quick questions so we can call you with the right trial plan for your business.
            </p>
          </div>
          <form className="campaign-form" onSubmit={handleFormSubmit}>
            <label htmlFor="name">Name</label>
            <input
              id="name"
              name="name"
              type="text"
              required
              value={formData.name}
              onChange={handleInputChange}
              onFocus={() => handleFormStart('name')}
            />

            <label htmlFor="business">Business Name</label>
            <input
              id="business"
              name="business"
              type="text"
              required
              value={formData.business}
              onChange={handleInputChange}
              onFocus={() => handleFormStart('business')}
            />

            <label htmlFor="email">Email</label>
            <input
              id="email"
              name="email"
              type="email"
              required
              value={formData.email}
              onChange={handleInputChange}
              onFocus={() => handleFormStart('email')}
            />

            <label htmlFor="phone">Phone</label>
            <input
              id="phone"
              name="phone"
              type="tel"
              required
              value={formData.phone}
              onChange={handleInputChange}
              onFocus={() => handleFormStart('phone')}
            />

            <label htmlFor="serviceFocus">What do you need most right now?</label>
            <select
              id="serviceFocus"
              name="serviceFocus"
              required
              value={formData.serviceFocus}
              onChange={handleInputChange}
              onFocus={() => handleFormStart('serviceFocus')}
            >
              <option value="">Select one</option>
              <option value="monthly_bookkeeping">Monthly bookkeeping support</option>
              <option value="book_cleanup">Book cleanup / catch-up</option>
              <option value="bookkeeping_plus_tax">Bookkeeping + tax support</option>
              <option value="controller_reporting">Controller-level reporting support</option>
            </select>

            <label htmlFor="monthlyVolume">Monthly transaction volume?</label>
            <select
              id="monthlyVolume"
              name="monthlyVolume"
              required
              value={formData.monthlyVolume}
              onChange={handleInputChange}
              onFocus={() => handleFormStart('monthlyVolume')}
            >
              <option value="">Select one</option>
              <option value="under_150">Under 150 transactions</option>
              <option value="150_500">150 - 500 transactions</option>
              <option value="500_1500">500 - 1,500 transactions</option>
              <option value="1500_plus">1,500+ transactions</option>
            </select>

            <label htmlFor="timeline">How soon do you want to start?</label>
            <select
              id="timeline"
              name="timeline"
              required
              value={formData.timeline}
              onChange={handleInputChange}
              onFocus={() => handleFormStart('timeline')}
            >
              <option value="">Select one</option>
              <option value="today">Today</option>
              <option value="this_week">This week</option>
              <option value="this_month">This month</option>
              <option value="just_researching">Just researching for now</option>
            </select>

            <label htmlFor="notes">Notes (Optional)</label>
            <textarea
              id="notes"
              name="notes"
              rows="4"
              value={formData.notes}
              onChange={handleInputChange}
              onFocus={() => handleFormStart('notes')}
            />

            <button type="submit" disabled={formState.loading}>
              {formState.loading ? 'Submitting...' : 'Get My Free Trial Plan'}
            </button>

            {formState.success && (
              <p className="campaign-form-success">
                Thanks. Your callback request was sent successfully.
              </p>
            )}
            {formState.error && (
              <p className="campaign-form-error">{formState.error}</p>
            )}
          </form>
        </div>
      </section>

      <section className="campaign-section campaign-footnote">
        <div className="campaign-wrap">
          <p>*Starting price applies to bookkeeping plans with up to 100 transactions per month.</p>
        </div>
      </section>
    </div>
  );
}

export default CampaignLanding;
